// statvocab — THE shared combat-stat vocabulary. Every surface that shows a
// combat stat (fight panel, item/weapon/creature sheets, worn-doll readout,
// codex halves, derived reference, hunt den rows) renders through these
// helpers so stats look identical everywhere: a short COLOR-CODED
// abbreviation chip with the full stat name in the tooltip. Colors come from
// css/theme.css --stat-* vars ONLY. Values are always SERVER-AUTHORED strings/numbers — the
// vocabulary is presentation only, never math.

import { el, tooltip } from './kit';

export const STATS: Record<string, { abbr: string; full: string }> = {
  hp: { abbr: 'hp', full: 'Hit points' },
  dmg: { abbr: 'dmg', full: 'Damage per swing' },
  swt: { abbr: 'swt', full: 'Swing time — seconds between attacks' },
  acc: { abbr: 'acc', full: 'Accuracy' },
  hit: { abbr: 'hit', full: 'Hit chance vs this target' },
  arm: { abbr: 'arm', full: 'Armor — blocks physical damage' },
  res: { abbr: 'res', full: 'Resist — blocks magic damage' },
  agi: { abbr: 'agi', full: 'Agility — dodge' },
  spd: { abbr: 'spd', full: 'Speed — closes combat distance (yd/s)' },
  rng: { abbr: 'rng', full: 'Range — yards' },
  str: { abbr: 'str', full: 'Strength' },
  dex: { abbr: 'dex', full: 'Dexterity' },
  int: { abbr: 'int', full: 'Intelligence' },
  vit: { abbr: 'vit', full: 'Vitality' },
  mana: { abbr: 'mana', full: 'Mana cost per attack' },
};

export function statColor(key: string): string {
  return key in STATS ? `var(--stat-${key})` : 'var(--text-dim)';
}

/** One compact stat chip: colored abbreviation + value, full name tooltip.
 *  Unknown keys render the key itself dim (forward-compatible with new
 *  server-served stat keys). */
export function statChip(key: string, value: unknown, tipExtra = ''): HTMLElement {
  const spec = STATS[key];
  const chip = el(
    'span',
    { class: 'stat-chip' },
    el('span', { class: 'stat-abbr', style: `color:${statColor(key)}`, text: spec?.abbr ?? key }),
    el('span', { class: 'stat-val', text: String(value ?? '?') }),
  );
  const full = spec?.full ?? key;
  tooltip(chip, () => (tipExtra ? `${full} — ${tipExtra}` : full));
  return chip;
}

// ---- stat badges ----------------------------------------------------------
// A larger game-style stat tile: an inline SVG glyph shaped per stat with the
// value rendered prominently ON the glyph (armor number inside its shield) or
// beside it. Same vocabulary/tooltip as statChip; glyph geometry is fixed art
// and EVERY color rides currentColor <- the --stat-* theme vars. Values are
// server-authored — presentation only, never math.

const SHIELD = 'M12 3 L19 6 V11.5 C19 16.4 15.6 19.7 12 21 C8.4 19.7 5 16.4 5 11.5 V6 Z';

const GLYPHS: Record<string, string> = {
  // arm: plain heater shield
  arm: `<path d="${SHIELD}" fill="currentColor" fill-opacity="0.16" stroke="currentColor" stroke-width="1.6"/>`,
  // res: shield carrying a faint rune-swirl near the crown (distinct from arm)
  res:
    `<path d="${SHIELD}" fill="currentColor" fill-opacity="0.16" stroke="currentColor" stroke-width="1.6"/>` +
    `<path d="M9.4 8.2 C10.6 5.9 14.2 5.9 15 7.9 C15.6 9.4 14.2 10.6 12.6 10" fill="none" stroke="currentColor" stroke-width="1.3" stroke-opacity="0.7" stroke-linecap="round"/>`,
  // dmg: sword — blade, crossguard, hilt, pommel
  dmg:
    '<path d="M20 4 L9 15" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>' +
    '<path d="M6.8 12.8 L11.2 17.2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>' +
    '<path d="M9 15 L5.8 18.2" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>' +
    '<circle cx="4.7" cy="19.3" r="1.4" fill="currentColor"/>',
  // swt: hourglass with a sand pile
  swt:
    '<path d="M7 3.8 H17 M7 20.2 H17" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>' +
    '<path d="M8.2 3.8 V7 C8.2 9.5 11 10.6 11 12 C11 13.4 8.2 14.5 8.2 17 V20.2 M15.8 3.8 V7 C15.8 9.5 13 10.6 13 12 C13 13.4 15.8 14.5 15.8 17 V20.2" fill="none" stroke="currentColor" stroke-width="1.4"/>' +
    '<path d="M9.8 18.6 h4.4 l-2.2 -2.8 Z" fill="currentColor"/>',
  // acc: crosshair
  acc:
    '<circle cx="12" cy="12" r="6" fill="none" stroke="currentColor" stroke-width="1.6"/>' +
    '<path d="M12 2.5 v3.4 M12 18.1 v3.4 M2.5 12 h3.4 M18.1 12 h3.4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>' +
    '<circle cx="12" cy="12" r="1.3" fill="currentColor"/>',
  // hit: target rings
  hit:
    '<circle cx="12" cy="12" r="8.2" fill="none" stroke="currentColor" stroke-width="1.6"/>' +
    '<circle cx="12" cy="12" r="4.6" fill="none" stroke="currentColor" stroke-width="1.4" stroke-opacity="0.75"/>' +
    '<circle cx="12" cy="12" r="1.6" fill="currentColor"/>',
  // agi: feather — vane + quill
  agi:
    '<path d="M19.3 4.7 C11.8 5.1 6.4 10.6 5 19.4 C13.8 18.2 19 12.9 19.6 5.1 Z" fill="currentColor" fill-opacity="0.16" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>' +
    '<path d="M5.6 18.8 L17 7.2" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>',
  // spd: boot + motion lines
  spd:
    '<path d="M10 3.8 h4.4 V11 c3.4 0.4 5.9 2.4 6.4 5.8 v1.9 a1.5 1.5 0 0 1 -1.5 1.5 H10 Z" fill="currentColor" fill-opacity="0.16" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>' +
    '<path d="M3 13.5 h4 M2 16.5 h5 M3 19.5 h4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>',
  // rng: bow, string and nocked arrow
  rng:
    '<path d="M7 3.6 C15 7.6 15 16.4 7 20.4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>' +
    '<path d="M7 3.6 L7 20.4" stroke="currentColor" stroke-width="1.1" stroke-opacity="0.65"/>' +
    '<path d="M5 12 H19.5 M19.5 12 L16 9.7 M19.5 12 L16 14.3" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>',
  // hp: heart
  hp: '<path d="M12 20.2 C6 15.4 3.8 11.6 5.4 8.4 C6.8 5.6 10.6 5.6 12 8.6 C13.4 5.6 17.2 5.6 18.6 8.4 C20.2 11.6 18 15.4 12 20.2 Z" fill="currentColor" fill-opacity="0.2" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>',
};

// glyphs roomy enough to carry the number inside (classic RPG shield-number);
// long values fall back beside the glyph
const OVERLAY = new Set(['arm', 'res', 'hp']);

/** One game-style stat tile: shaped SVG glyph + prominent value, full stat
 *  name tooltip (tipExtra appended). Unknown keys get a rounded plate with
 *  the abbreviation and the value beside. */
export function statBadge(key: string, value: unknown, tipExtra = ''): HTMLElement {
  const spec = STATS[key];
  const val = String(value ?? '?');
  const overlay = OVERLAY.has(key) && val.length <= 3;
  const glyph = el('span', { class: 'stat-badge-glyph', style: `color:${statColor(key)}` });
  const inner =
    GLYPHS[key] ??
    '<rect x="4" y="4" width="16" height="16" rx="4" fill="currentColor" fill-opacity="0.16" stroke="currentColor" stroke-width="1.5"/>';
  glyph.innerHTML = `<svg viewBox="0 0 24 24" aria-hidden="true">${inner}</svg>`;
  if (!GLYPHS[key]) glyph.append(el('span', { class: 'stat-badge-abbr', text: spec?.abbr ?? key }));
  const badge = el('span', { class: 'stat-badge' }, glyph);
  if (overlay) glyph.append(el('span', { class: 'stat-badge-over', text: val }));
  else badge.append(el('span', { class: 'stat-badge-val', text: val }));
  const full = spec?.full ?? key;
  tooltip(badge, () => (tipExtra ? `${full} — ${tipExtra}` : full));
  return badge;
}

/** A wrapping row of stat chips from server-shaped {stat, value} entries
 *  (null/invalid entries skipped). */
export function statRow(entries: Array<{ stat?: unknown; value?: unknown } | null | undefined>): HTMLElement {
  const row = el('div', { class: 'stat-row' });
  for (const e of entries) {
    if (!e || typeof e !== 'object' || e.stat == null || e.value == null) continue;
    row.append(statChip(String(e.stat), e.value));
  }
  return row;
}
